import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { Mail, MapPin, Check, X } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Card, CardContent, Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import Convocatoria from './Convocatoria';
import AvisoDeportesFaltantes from '../deportes/AvisoDeportesFaltantes';
import { getMisInvitacionesTorneo, aceptarInvitacion, rechazarInvitacion } from '../../lib/services/torneoInvitaciones';
import { supabase } from '../../lib/supabase';
import { parseLocalDate } from '../../lib/dateUtils';
import { fixMojibake } from '../../lib/utils';

// La bandeja: lo que llega de la red y todavia no contestas.
//
// Una invitacion no es un torneo -- es una decision pendiente -- asi que tiene pantalla
// propia y no se mezcla con los torneos donde el club ya juega. Al aceptar, el torneo
// se muda a "Mis torneos" y esta bandeja se vacia.
//
// "Ver convocatoria" antes de decidir no es un extra: si no convocaron la categoria de
// tus grupos, aceptar no sirve de nada.

const formatFecha = (fecha) => {
  if (!fecha) return 'Sin fecha';
  try {
    return format(parseLocalDate(fecha) || new Date(), 'dd MMM yyyy', { locale: es });
  } catch { return 'Fecha no disponible'; }
};

const MyklubbInvitaciones = () => {
  const navigate = useNavigate();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [invitaciones, setInvitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);
  const [convocatoria, setConvocatoria] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data } = await getMisInvitacionesTorneo();
    setInvitaciones(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const verConvocatoria = async (inv) => {
    setConvocatoria({ cargando: true });
    const { data, error } = await supabase.rpc('get_convocatoria', { p_torneo_id: inv.torneo_id });
    setConvocatoria(error ? { error: error.message } : data);
  };

  const handleAceptar = async (inv) => {
    setProcesando(inv.id);
    try {
      const { error } = await aceptarInvitacion(inv.id);
      if (error) throw error;
      await cargar();
      // Aceptada, el torneo ya vive del otro lado: se lleva al usuario ahi en vez de
      // dejarlo en una bandeja que acaba de vaciarse.
      navigate(`/torneos-myklubb/${inv.torneo_id}`, { state: { nombre: inv.torneo_nombre } });
    } catch (error) {
      console.error('Error aceptando invitación:', error);
      await alert('Error', 'No se pudo aceptar la invitación. Intenta de nuevo.');
    } finally {
      setProcesando(null);
    }
  };

  const handleRechazar = async (inv) => {
    const nombre = fixMojibake(inv.torneo_nombre || 'este torneo');
    const ok = await confirm(
      'Rechazar invitación',
      `¿Rechazar la invitación a "${nombre}"?`,
      { destructive: true, confirmText: 'Rechazar' }
    );
    if (!ok) return;
    setProcesando(inv.id);
    try {
      const { error } = await rechazarInvitacion(inv.id);
      if (error) throw error;
      await cargar();
    } catch (error) {
      console.error('Error rechazando invitación:', error);
      await alert('Error', 'No se pudo rechazar la invitación. Intenta de nuevo.');
    } finally {
      setProcesando(null);
    }
  };

  return (
    <FeaturedLayout
      title="Invitaciones"
      subtitle="Torneos a los que te invitaron y todavía no respondes."
      refreshAction={cargar}
    >
      {loading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Cargando invitaciones...</p>
      ) : invitaciones.length === 0 ? (
        <Card>
          <CardContent className="space-y-3 py-14 text-center">
            <Mail className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
            <p className="font-medium">No tienes invitaciones pendientes</p>
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Cuando una liga u otro club invite al tuyo a su torneo, aparecerá aquí para que
              leas la convocatoria y decidas.
            </p>
            <Button size="sm" variant="outline" onClick={() => navigate('/torneos-myklubb')}>
              Ver mis torneos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {invitaciones.map((inv) => {
            const ocupado = procesando === inv.id;
            return (
              <Card key={inv.id} className="flex h-full flex-col">
                <CardContent className="flex flex-grow flex-col gap-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="truncate text-base font-semibold leading-tight">
                      {fixMojibake(inv.torneo_nombre || 'Torneo sin nombre')}
                    </h3>
                    <Badge variant="outline" className="shrink-0 text-[11px] font-normal">Pendiente</Badge>
                  </div>
                  {inv.torneo_sede && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {fixMojibake(inv.torneo_sede)}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">{formatFecha(inv.torneo_fecha_inicio)}</p>
                  <AvisoDeportesFaltantes deportes={inv.deportes_faltantes} />

                  <button
                    type="button"
                    onClick={() => verConvocatoria(inv)}
                    className="self-start text-xs font-medium text-primary underline-offset-2 hover:underline"
                  >
                    Ver convocatoria
                  </button>

                  <div className="mt-auto flex gap-2 pt-2">
                    <Button size="sm" className="h-8 flex-1" onClick={() => handleAceptar(inv)} disabled={ocupado}>
                      <Check className="mr-1.5 h-3.5 w-3.5" /> Aceptar
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 flex-1" onClick={() => handleRechazar(inv)} disabled={ocupado}>
                      <X className="mr-1.5 h-3.5 w-3.5" /> Rechazar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!convocatoria} onOpenChange={(v) => { if (!v) setConvocatoria(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Convocatoria</DialogTitle></DialogHeader>
          {convocatoria?.cargando ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Cargando...</p>
          ) : convocatoria?.error ? (
            <p className="py-6 text-center text-sm text-destructive">{convocatoria.error}</p>
          ) : (
            <Convocatoria convocatoria={convocatoria} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default MyklubbInvitaciones;
